import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideNoticeUser: React.FC = () => {
  return (
    <GuidePageLayout
      title="通知を確認・操作する（ユーザー）"
      description="自分宛ての通知の確認、個人通知の削除、お知らせの解決を行います。"
      breadcrumbs={[{ label: '通知管理', path: '/guide/notice/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[['ログイン状態', 'ログイン済みであること（権限不問）']]}
        />
      </GuideSection>

      {/* 通知パネル（UserNotice） */}
      <GuideSection title="ホーム画面の通知パネルを使う">
        <GuideStepList
          steps={[
            { label: '各ドメインのホーム画面に通知パネルが表示される' },
            {
              label:
                'タブ（通知・お知らせ・重要なお知らせ・セキュリティチェック）を押下してパネルを展開する',
            },
            { label: '選択したタブに該当する通知が一覧表示される' },
            { label: '通知ごとに削除・解決・リンク遷移等の操作が可能' },
            { label: '選択中のタブをもう一度押下するとパネルが閉じる（最小化）' },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <GuideInfoBox>
            タブが未選択の状態ではパネルは最小化されます。タブを選択すると展開されます。
          </GuideInfoBox>
          <GuideInfoBox>タブのバッジには新着（未読）の通知件数が表示されます。</GuideInfoBox>
          <GuideInfoBox>
            お知らせ・重要なお知らせタブでは「過去の通知を見る」から解決済み通知を確認できます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>

      {/* 通知確認 */}
      <GuideSection title="自分宛ての通知を確認する（BUC-N07）">
        <GuideStepList
          steps={[
            { label: 'ヘッダーの通知アイコンを押下して通知ポップアップを開く' },
            { label: '通知が種別ごとにグループ化されて表示される' },
            { label: '未解決の通知を確認する' },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <GuideInfoBox>「最新」マークが付いた通知は新着の通知です。</GuideInfoBox>
          <GuideInfoBox>期限が近い通知は優先度が引き上げられて上位に表示されます。</GuideInfoBox>
        </Stack>
      </GuideSection>

      {/* 通知削除 */}
      <GuideSection title="個人向け通知を削除する（BUC-N08）">
        <GuideStepList
          steps={[
            { label: '通知一覧画面でNOTICEグループの通知を確認する' },
            {
              label: '対象通知の「削除」ボタンを押下する',
              content: (
                <GuideMockContainer label="削除ボタン">
                  <GuideMockButton
                    label="削除"
                    color="error"
                    startIcon={<DeleteIcon />}
                    size="small"
                  />
                </GuideMockContainer>
              ),
            },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <GuideInfoBox>
            お知らせ（INFO）/重要なお知らせ（IMPORTANT）には削除ボタンは表示されません。それらは「解決」で対応します。
          </GuideInfoBox>
        </Stack>
      </GuideSection>

      {/* 解決 */}
      <GuideSection title="お知らせを解決済みにする（BUC-N09）">
        <GuideStepList
          steps={[
            { label: '通知一覧画面でINFO/IMPORTANTグループの通知を確認する' },
            { label: '内容を読み、確認が完了する' },
            {
              label: '「解決」ボタンを押下する',
              content: (
                <GuideMockContainer label="解決ボタン">
                  <GuideMockButton
                    label="解決"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    size="small"
                  />
                </GuideMockContainer>
              ),
            },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <GuideInfoBox>
            個人の解決は自分だけに影響します。他ユーザーには影響しません。
          </GuideInfoBox>
          <GuideInfoBox>
            管理者による一括解決が行われた場合は、自動的に全員分が解決済みとなります。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideNoticeUser;
